import React from 'react';
import { X, Search } from 'lucide-react';

export const QuantityPickerModal = ({ isOpen, onClose, onSelect, currentValue }) => {
  const [customMode, setCustomMode] = React.useState(false);
  const [customValue, setCustomValue] = React.useState('');

  if (!isOpen) return null;

  const quickPicks = ['1', '2', '3', '4'];

  const handleSelect = (value) => {
    onSelect(value);
    onClose();
  };

  const handleCustomSubmit = () => {
    if (customValue) {
      onSelect(customValue);
      onClose();
      setCustomMode(false);
      setCustomValue('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl w-full max-w-md p-6 space-y-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Select Quantity</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!customMode ? (
          <div className="grid grid-cols-2 gap-3">
            {quickPicks.map((qty) => (
              <button
                key={qty}
                onClick={() => handleSelect(qty)}
                className={`py-3 px-4 rounded-lg font-semibold text-lg transition-all ${
                  currentValue === qty
                    ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {qty}
              </button>
            ))}
            <button
              onClick={() => setCustomMode(true)}
              className="col-span-2 py-3 px-4 rounded-lg font-semibold text-amber-600 bg-amber-50 hover:bg-amber-100 transition-all"
            >
              Custom...
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="text"
              className="input-field w-full text-lg"
              placeholder="Enter quantity (e.g., 1, 2, 4 pair)"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setCustomMode(false);
                  setCustomValue('');
                }}
                className="flex-1 py-2 px-4 rounded-lg font-semibold bg-gray-200 text-gray-700"
              >
                Back
              </button>
              <button
                onClick={handleCustomSubmit}
                className="flex-1 py-2 px-4 rounded-lg font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-white"
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const PurityPickerModal = ({ isOpen, onClose, onSelect, currentValue, purities }) => {
  if (!isOpen) return null;

  const defaultPurities = purities && purities.length > 0 ? purities : ['18K', '22K'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl w-full max-w-md p-6 space-y-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Select Purity</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {defaultPurities.map((purity) => (
            <button
              key={purity}
              onClick={() => {
                onSelect(purity);
                onClose();
              }}
              className={`py-3 px-4 rounded-lg font-semibold text-lg transition-all ${
                currentValue === purity
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              {purity}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const FinishColorPickerModal = ({ isOpen, onClose, onSelect, currentValue, colors }) => {
  if (!isOpen) return null;

  const defaultColors = colors && colors.length > 0 ? colors : ['Yellow Gold', 'Rose Gold'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl w-full max-w-md p-6 space-y-4 animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Select Finish Color</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2">
          {defaultColors.map((color) => (
            <button
              key={color}
              onClick={() => {
                onSelect(color);
                onClose();
              }}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-left transition-all ${
                currentValue === color
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              {color}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const VendorPickerModal = ({ isOpen, onClose, onSelect, currentValue, vendors }) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  if (!isOpen) return null;

  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.short_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Select Vendor</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            className="input-field w-full pl-10"
            placeholder="Search vendor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="space-y-2 overflow-y-auto flex-1">
          <button
            onClick={() => {
              onSelect('');
              onClose();
            }}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-left transition-all ${
              currentValue === ''
                ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            -- No Vendor --
          </button>
          {filteredVendors.map((vendor) => (
            <button
              key={vendor.id}
              onClick={() => {
                onSelect(vendor.short_code);
                onClose();
              }}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-left transition-all ${
                currentValue === vendor.short_code
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              <div className="font-bold">{vendor.short_code}</div>
              <div className="text-sm opacity-80">{vendor.name}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
